import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { displayError, displaySuccess } from "components/Toast/toast.component";
import PrioritizeComponent from "components/prioritize/prioritize.component";
import { CareerCategory } from "enums/glimps.enums";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { getUserBadgeEntries } from "services/badges.service";
import { ListPrioritize, completeOnBoard } from "services/on-board";
import Button from "components/button/button";

const UserOnBoardComplete: React.FC<{ position: "start" | "center" }> = ({
  position,
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  const [isFetchingBadges, setIsFetchingBadges] = useState(false);
  const [attemptedSave, setAttemptedSave] = useState(false);
  const queryClient = useQueryClient();
  const completedOnBoard = useMutation({
    mutationFn: () => completeOnBoard(),
    retry: 3,
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 5000),
    onSuccess: async (data) => {
      try {
        const localStorageValue = localStorage.getItem("userInfo");
        if (localStorageValue) {
          const userInfo = JSON.parse(localStorageValue);
          if (userInfo?.user?.profile) {
            userInfo.user.profile.onboarding_completed = true;
            localStorage.setItem("userInfo", JSON.stringify(userInfo));
          }
          displaySuccess(data.message);
        }
      } catch (e) {
        console.error("[onboard-complete] Failed to update localStorage", e);
        displaySuccess(data.message);
      }
    },
    onError: (error: any) => {
      const message =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        "Failed to complete onboarding. Please try again.";
      displayError(message);
    },
  });
  const { data: listofSortedData } = useQuery({
    queryKey: ["ListPrioritize"],
    queryFn: () => ListPrioritize(),
    retry: false,
    refetchOnWindowFocus: false,
  });
  const unsortedCount = listofSortedData?.filter(
    (x) => x.category === CareerCategory.unsorted
  ).length ?? 0;
  const hasUnsorted = unsortedCount > 0;

  const onBoardComplete = async () => {
    if (hasUnsorted) {
      setAttemptedSave(true);
      return;
    }
    let userInfo: any = null;
    try {
      const localStorageValue = localStorage.getItem("userInfo");
      userInfo = localStorageValue ? JSON.parse(localStorageValue) : null;
    } catch (e) {
      console.error("[onboard-complete] Failed to parse userInfo", e);
    }
    const isAlreadyCompleted = userInfo?.user?.profile?.onboarding_completed;

    if (location.pathname.includes("on-boarding")) {
      if (!isAlreadyCompleted) {
        try {
          await completedOnBoard.mutateAsync();

          setIsFetchingBadges(true);
          queryClient.invalidateQueries({ queryKey: ["my-earned-badges"] });
          window.location.href = "/user-careers?tab=preview";
        } catch (err: any) {
          console.error("[onboard-complete] Failed to complete onboarding", err);
          displayError(
            err?.response?.data?.error ||
            "Failed to complete onboarding. Please try again."
          );
        } finally {
          setIsFetchingBadges(false);
        }
      } else {
        navigate("/user-careers?tab=preview");
      }
    } else {
      if (location.pathname.includes("prioritize/unlock-next")) {
        navigate("/user-careers");
      } else if (location.pathname.includes("user-careers/unlock-next")) {
        navigate("/user-careers?tab=preview");
      } else {
        navigate("/user-careers");
      }
    }
  };

  const isDisabled = isFetchingBadges || completedOnBoard.isPending;

  useEffect(() => {
    if (!hasUnsorted && attemptedSave) {
      setAttemptedSave(false);
    }
  }, [hasUnsorted, attemptedSave]);

  return (
    <div className="flex flex-col bg-white max-w-[1280px] mx-auto w-full pt-8 pb-2">
      {/* Top bar — only show step indicator during initial onboarding */}
      {location.pathname.includes("on-boarding") && (
        <div className="flex items-center justify-center px-6">
          <span className="text-body-sm-emphasis text-text-muted">Step 3 of 3</span>
        </div>
      )}

      {/* Main content */}
      <div className="px-2 md:px-6 mt-4">
        <PrioritizeComponent showUnsortedError={attemptedSave && hasUnsorted} />

        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-3 sm:gap-4 mt-4 px-5">
          <Button
            variant="primary"
            disabled={isDisabled}
            onClick={() => onBoardComplete()}
          >
            {isFetchingBadges || completedOnBoard.isPending
              ? "Saving..."
              : "Save"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default UserOnBoardComplete;
